import React, { useState, useEffect } from 'react'
import { Save, FolderOpen, Search } from 'lucide-react'
import usePracticeStore from '../../stores/practiceStore'
import AVAILABLE_TAGS from '../../config/tags'
import {
  savePracticeSession,
  loadPracticeSession,
  getUserPracticeSessions,
  getPublicPracticeSessions,
  deletePracticeSession
} from '../../services/practiceSessionsApi'

const PracticeSessionManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('save') // 'save', 'load'
  const [sessionName, setSessionName] = useState('')
  const [sessionDescription, setSessionDescription] = useState('')
  const [sportType, setSportType] = useState('soccer')
  const [isPublic, setIsPublic] = useState(false)
  const [selectedTags, setSelectedTags] = useState([])
  const [tagSearch, setTagSearch] = useState('')
  const [showTagDropdown, setShowTagDropdown] = useState(false)
  const [savedSessions, setSavedSessions] = useState([])
  const [publicSessions, setPublicSessions] = useState([])
  const [loadView, setLoadView] = useState('my') // 'my', 'public'
  const [loadSearch, setLoadSearch] = useState('')
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [isLoadingPublicSessions, setIsLoadingPublicSessions] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  
  const {
    tiles,
    notes,
    sessionName: currentSessionName,
    originalLoadedSession,
    setTiles,
    setNotes,
    setSessionName: setCurrentSessionName,
    setOriginalLoadedSession
  } = usePracticeStore()

  // Load saved sessions when modal opens for load
  useEffect(() => {
    if (showModal && modalType === 'load') {
      if (loadView === 'my') {
        loadSavedSessions()
      } else if (loadView === 'public') {
        loadPublicSessions()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, modalType, loadView])

  // Close tag dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTagDropdown && !event.target.closest('.tag-search-container')) {
        setShowTagDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTagDropdown])

  // Initialize session name from store
  useEffect(() => {
    if (currentSessionName) {
      setSessionName(currentSessionName)
    }
  }, [currentSessionName])

  const loadSavedSessions = async () => {
    setIsLoadingSessions(true)
    try {
      const sessions = await getUserPracticeSessions()
      setSavedSessions(sessions)
    } catch (error) {
      console.error('Error loading sessions:', error)
      setSavedSessions([])
    } finally {
      setIsLoadingSessions(false)
    }
  }

  const loadPublicSessions = async () => {
    setIsLoadingPublicSessions(true)
    try {
      const sessions = await getPublicPracticeSessions()
      setPublicSessions(sessions)
    } catch (error) {
      console.error('Error loading public sessions:', error)
      setPublicSessions([])
    } finally {
      setIsLoadingPublicSessions(false)
    }
  }

  const handleSave = async () => {
    if (sessionName.trim()) {
      setIsSaving(true)
      try {
        // Check if a session with the same name already exists
        const existingSession = savedSessions.find(session => session.name.trim() === sessionName.trim())
        
        if (existingSession && (!originalLoadedSession.id || originalLoadedSession.id !== existingSession.id)) {
          // Show confirmation dialog
          const shouldOverwrite = confirm(
            `A practice session named "${sessionName.trim()}" already exists. Do you want to overwrite it?`
          )
          
          if (!shouldOverwrite) {
            setIsSaving(false)
            return // User cancelled, don't save
          }
        }

        const sessionData = {
          id: originalLoadedSession.id && originalLoadedSession.name === sessionName.trim() 
            ? originalLoadedSession.id 
            : null, // Create new if name changed
          name: sessionName.trim(),
          description: sessionDescription.trim(),
          sportType,
          tags: selectedTags,
          tiles,
          notes
        }

        // If name changed from original, create new record
        const nameHasChanged = originalLoadedSession.id && originalLoadedSession.name !== sessionName.trim()
        const sessionIdToUse = nameHasChanged ? null : (originalLoadedSession.id || existingSession?.id)

        const savedSession = await savePracticeSession(
          { ...sessionData, id: sessionIdToUse },
          isPublic
        )

        // Update store
        setCurrentSessionName(savedSession.name)
        setOriginalLoadedSession({ id: savedSession.id, name: savedSession.name })

        // Reset form
        setShowModal(false)
        setSessionName('')
        setSessionDescription('')
        setSportType('soccer')
        setIsPublic(false)
        setSelectedTags([])
        setTagSearch('')
        setShowTagDropdown(false)

        // Reload sessions list
        await loadSavedSessions()
      } catch (error) {
        console.error('Error saving session:', error)
        alert('Failed to save practice session: ' + error.message)
      } finally {
        setIsSaving(false)
      }
    }
  }

  const handleLoad = async (sessionId) => {
    try {
      // Determine if this is from "My Sessions" (user's own) or "Public Sessions" tab
      const isUserOwnSession = loadView === 'my'
      const session = await loadPracticeSession(sessionId, isUserOwnSession)
      
      // Load session data into store
      setTiles(session.tiles || [])
      setNotes(session.notes || '')
      setCurrentSessionName(session.name)
      setOriginalLoadedSession({ id: session.id, name: session.name })
      
      // Update form fields
      setSessionName(session.name)
      setSessionDescription(session.description || '')
      setSportType(session.sportType || 'soccer')
      setIsPublic(session.isPublic || false)
      setSelectedTags(session.tags || [])
      
      setShowModal(false)
      setLoadSearch('')
    } catch (error) {
      console.error('Error loading session:', error)
      alert('Failed to load practice session: ' + error.message)
    }
  }

  const handleDelete = async (sessionId, e) => {
    e.stopPropagation()
    if (confirm('Are you sure you want to delete this practice session?')) {
      try {
        await deletePracticeSession(sessionId)
        await loadSavedSessions()
      } catch (error) {
        console.error('Error deleting session:', error)
        alert('Failed to delete practice session: ' + error.message)
      }
    }
  }

  const openModal = (type) => {
    setModalType(type)
    setShowModal(true)
    
    if (type === 'save') {
      // Pre-fill with current session name
      setSessionName(currentSessionName || 'Practice Session Title')
      setSessionDescription('')
      setSportType('soccer')
      setIsPublic(false)
      setSelectedTags([])
    } else if (type === 'load') {
      setLoadView('my')
      setLoadSearch('')
    }
  }

  // Filter tags based on search
  const filteredTags = AVAILABLE_TAGS.filter(tag =>
    tag.toLowerCase().includes(tagSearch.toLowerCase()) &&
    !selectedTags.includes(tag)
  )

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag))
    } else {
      setSelectedTags([...selectedTags, tag])
    }
    setTagSearch('')
    setShowTagDropdown(false)
  }

  const removeTag = (tag) => {
    setSelectedTags(selectedTags.filter(t => t !== tag))
  }

  const filterSessions = (sessions, searchTerm) => {
    if (!searchTerm.trim()) {
      return sessions
    }
    const searchLower = searchTerm.toLowerCase().trim()
    return sessions.filter(session => {
      // Search in name
      if (session.name?.toLowerCase().includes(searchLower)) {
        return true
      }
      // Search in description
      if (session.description?.toLowerCase().includes(searchLower)) {
        return true
      }
      // Search in sport type
      if (session.sportType?.toLowerCase().includes(searchLower)) {
        return true
      }
      // Search in tags
      if (session.tags && Array.isArray(session.tags)) {
        const tagMatch = session.tags.some(tag => 
          tag.toLowerCase().includes(searchLower)
        )
        if (tagMatch) {
          return true
        }
      }
      return false
    })
  }

  return (
    <>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => openModal('save')}
          className="flex items-center justify-center space-x-1 w-32 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
        >
          <Save size={14} />
          <span>Save</span>
        </button>
        
        <button
          onClick={() => openModal('load')}
          className="flex items-center justify-center space-x-1 w-32 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          <FolderOpen size={14} />
          <span>Load</span>
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            {modalType === 'save' && (
              <>
                <h3 className="text-lg font-semibold mb-4">Save Practice Session</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Session Name
                    </label>
                    <input
                      type="text"
                      value={sessionName}
                      onChange={(e) => setSessionName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter session name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={sessionDescription}
                      onChange={(e) => setSessionDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Enter session description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sport Type
                    </label>
                    <select
                      value={sportType}
                      onChange={(e) => setSportType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="soccer">Soccer</option>
                      <option value="football">Football</option>
                      <option value="basketball">Basketball</option>
                      <option value="hockey">Hockey</option>
                      <option value="lacrosse">Lacrosse</option>
                      <option value="rugby">Rugby</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <div className="relative tag-search-container">
                      <input
                        type="text"
                        value={tagSearch}
                        onChange={(e) => {
                          setTagSearch(e.target.value)
                          setShowTagDropdown(true)
                        }}
                        onFocus={() => setShowTagDropdown(true)}
                        placeholder="Search and select tags..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {showTagDropdown && filteredTags.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {filteredTags.map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => toggleTag(tag)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      )}
                      {showTagDropdown && tagSearch && filteredTags.length === 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 text-sm text-gray-500">
                          No matching tags found
                        </div>
                      )}
                    </div>
                    {selectedTags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedTags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-3 py-1 text-sm bg-blue-500 text-white rounded-full"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => removeTag(tag)}
                              className="ml-2 hover:text-gray-200 focus:outline-none"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isPublic"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="isPublic" className="text-sm font-medium text-gray-700">
                      Make this session public
                    </label>
                  </div>
                  {isPublic && (
                    <p className="text-xs text-gray-500">
                      Public sessions can be viewed and loaded by all users
                    </p>
                  )}
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            )}

            {modalType === 'load' && (
              <>
                <h3 className="text-lg font-semibold mb-4">Load Practice Session</h3>
                
                {/* Tab Navigation */}
                <div className="flex space-x-2 mb-4 border-b border-gray-200 flex-shrink-0">
                  <button
                    onClick={() => {
                      setLoadView('my')
                      setLoadSearch('')
                    }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      loadView === 'my'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    My Sessions
                  </button>
                  <button
                    onClick={() => {
                      setLoadView('public')
                      setLoadSearch('')
                    }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      loadView === 'public'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Public Sessions
                  </button>
                </div>

                {/* My Sessions View */}
                {loadView === 'my' && (
                  <div className="space-y-2 flex-1 flex flex-col min-h-0">
                    {/* Search Input */}
                    <div className="relative mb-3 flex-shrink-0">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        value={loadSearch}
                        onChange={(e) => setLoadSearch(e.target.value)}
                        placeholder="Search by name, description, tags, or sport type..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
                      {isLoadingSessions ? (
                        <p className="text-gray-500 text-center py-4">Loading sessions...</p>
                      ) : (() => {
                        const filteredSessions = filterSessions(savedSessions, loadSearch)
                        return filteredSessions.length === 0 ? (
                          <p className="text-gray-500 text-center py-4">
                            {loadSearch.trim() ? 'No sessions match your search' : 'No saved practice sessions'}
                          </p>
                        ) : (
                          filteredSessions.map((session) => (
                            <div
                              key={session.id}
                              onClick={() => handleLoad(session.id)}
                              className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                              <div className="font-medium flex items-center space-x-2">
                                <span>{session.name}</span>
                                {session.isPublic && (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                    Public
                                  </span>
                                )}
                                {session.sportType && (
                                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded capitalize">
                                    {session.sportType}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">{session.description}</div>
                              {session.tags && session.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {session.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="flex items-center justify-between mt-2">
                                <div className="text-xs text-gray-400">
                                  {new Date(session.updatedAt).toLocaleString()}
                                </div>
                                <button
                                  onClick={(e) => handleDelete(session.id, e)}
                                  className="text-xs text-red-500 hover:text-red-700"
                                  title="Delete session"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))
                        )
                      })()}
                    </div>
                  </div>
                )}

                {/* Public Sessions View */}
                {loadView === 'public' && (
                  <div className="space-y-2 flex-1 flex flex-col min-h-0">
                    {/* Search Input */}
                    <div className="relative mb-3 flex-shrink-0">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        value={loadSearch}
                        onChange={(e) => setLoadSearch(e.target.value)}
                        placeholder="Search by name, description, tags, or sport type..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
                      {isLoadingPublicSessions ? (
                        <p className="text-gray-500 text-center py-4">Loading public sessions...</p>
                      ) : (() => {
                        const filteredSessions = filterSessions(publicSessions, loadSearch)
                        return filteredSessions.length === 0 ? (
                          <p className="text-gray-500 text-center py-4">
                            {loadSearch.trim() ? 'No sessions match your search' : 'No public practice sessions available'}
                          </p>
                        ) : (
                          filteredSessions.map((session) => (
                            <div
                              key={session.id}
                              onClick={() => handleLoad(session.id)}
                              className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                              <div className="font-medium flex items-center space-x-2">
                                <span>{session.name}</span>
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                  Public
                                </span>
                                {session.sportType && (
                                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded capitalize">
                                    {session.sportType}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">{session.description}</div>
                              {session.tags && session.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {session.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="text-xs text-gray-400 mt-1">
                                {new Date(session.updatedAt).toLocaleString()}
                              </div>
                            </div>
                          ))
                        )
                      })()}
                    </div>
                  </div>
                )}

                <div className="flex justify-end mt-6 flex-shrink-0">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default PracticeSessionManager

